// apps/client/src/world/objectBounds.ts (light and look, 2026-10-16) — the world box of an object's view, contact blobs left out: an
// instanced view has no meshes of its own, so its proxies' templates stand in. The ground map (shade, lamp pools) and the occlusion fade
// both measure objects this way.
import * as THREE from 'three';
import { InstanceProxy } from './objectView';

const templateBoxes = new WeakMap<THREE.Object3D, THREE.Box3>();
/** A template's box in its own space (it is never in a scene), without its contact blobs. Cached: templates never change. */
export function templateBox(template: THREE.Object3D): THREE.Box3 {
  let box = templateBoxes.get(template);
  if (!box) {
    template.updateMatrixWorld(true);
    const inv = template.matrixWorld.clone().invert();
    box = meshesBox(template, inv);
    templateBoxes.set(template, box);
  }
  return box;
}
/** The union of the meshes under `root` (not blobs, not hidden ones), each through `pre` × its world matrix. */
function meshesBox(root: THREE.Object3D, pre: THREE.Matrix4 | null): THREE.Box3 {
  const box = new THREE.Box3(), b = new THREE.Box3(), m = new THREE.Matrix4();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || mesh.userData.blob || !mesh.visible) return;
    mesh.geometry.computeBoundingBox();
    b.copy(mesh.geometry.boundingBox!).applyMatrix4(pre ? m.multiplyMatrices(pre, mesh.matrixWorld) : mesh.matrixWorld);
    box.union(b);
  });
  return box;
}
/** The occlusion fade's upright cylinder of a filed view (engine/shader/fade.ts): (x, z, −radius, top) from its parts that rise at least
 *  `minHeight` above its base (the view's own height), so a fence, a flower bed or a plinth never widens it; null when no part does. */
export function fadeCylinder(view: THREE.Object3D, minHeight: number): [number, number, number, number] | null {
  view.updateWorldMatrix(true, true);
  const floor = view.getWorldPosition(new THREE.Vector3()).y + minHeight, box = new THREE.Box3(), b = new THREE.Box3();
  view.traverse((o) => {
    if (o instanceof InstanceProxy) {
      for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return;
      b.copy(templateBox(o.template)).applyMatrix4(o.matrixWorld);
    } else {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || mesh.userData.blob || !mesh.visible) return;
      mesh.geometry.computeBoundingBox();
      b.copy(mesh.geometry.boundingBox!).applyMatrix4(mesh.matrixWorld);
    }
    if (!b.isEmpty() && b.max.y >= floor) box.union(b);
  });
  if (box.isEmpty()) return null;
  return [(box.min.x + box.max.x) / 2, (box.min.z + box.max.z) / 2, -Math.max(box.max.x - box.min.x, box.max.z - box.min.z) / 2, box.max.y];
}
/** The world box of a filed view: its own meshes and every visible instance proxy's template. Empty when nothing shows. */
export function viewBounds(view: THREE.Object3D): THREE.Box3 {
  view.updateWorldMatrix(true, true);
  const box = meshesBox(view, null), b = new THREE.Box3();
  view.traverse((o) => {
    if (!(o instanceof InstanceProxy)) return;
    for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return;
    box.union(b.copy(templateBox(o.template)).applyMatrix4(o.matrixWorld));
  });
  return box;
}
